/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

/** Max user-authored prompts per thread (including the first message after attach). */
export const COMMAND_INTELLIGENCE_MAX_USER_PROMPTS = 15;

/**
 * When the latest assistant turn reports inputTokens at or above this cap, block further sends
 * until the user resets the chat (same idea as Cursor context window pressure).
 */
export const COMMAND_INTELLIGENCE_MAX_INPUT_CONTEXT_TOKENS = 128000;

/**
 * @param {Array<{ role?: string, metadata?: object }>} messages
 * @returns {{ inputTokens: number, outputTokens: number, totalTokens: number } | null}
 */
export const getLatestAssistantUsage = messages => {
  if (!Array.isArray(messages)) {
    return null;
  }

  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];

    if (m?.role === 'comparison' && !m?.isStreaming && Array.isArray(m.sides)) {
      let bestInput = 0;
      let bestOut = 0;
      let bestTotal = 0;

      for (const side of m.sides) {
        const u = side?.metadata?.usage;

        if (!u || typeof u !== 'object') {
          continue;
        }

        const inputTokens = typeof u.inputTokens === 'number' && !Number.isNaN(u.inputTokens) ? u.inputTokens : 0;
        const outputTokens = typeof u.outputTokens === 'number' && !Number.isNaN(u.outputTokens) ? u.outputTokens : 0;
        const totalTokens =
          typeof u.totalTokens === 'number' && !Number.isNaN(u.totalTokens) && u.totalTokens > 0
            ? u.totalTokens
            : inputTokens + outputTokens;

        if (inputTokens > bestInput) {
          bestInput = inputTokens;
          bestOut = outputTokens;
          bestTotal = totalTokens;
        }
      }

      if (bestInput + bestOut + bestTotal > 0) {
        return { inputTokens: bestInput, outputTokens: bestOut, totalTokens: bestTotal };
      }

      continue;
    }

    if (m?.role !== 'assistant' || m?.isStreaming) {
      continue;
    }

    const u = m?.metadata?.usage;

    if (!u || typeof u !== 'object') {
      continue;
    }

    const inputTokens = typeof u.inputTokens === 'number' && !Number.isNaN(u.inputTokens) ? u.inputTokens : 0;
    const outputTokens = typeof u.outputTokens === 'number' && !Number.isNaN(u.outputTokens) ? u.outputTokens : 0;
    const totalTokens =
      typeof u.totalTokens === 'number' && !Number.isNaN(u.totalTokens) && u.totalTokens > 0
        ? u.totalTokens
        : inputTokens + outputTokens;

    if (inputTokens + outputTokens + totalTokens === 0) {
      continue;
    }

    return { inputTokens, outputTokens, totalTokens };
  }

  return null;
};

/**
 * @param {Array<{ role?: string, text?: string, content?: string, isError?: boolean }>} messages
 * @returns {number}
 */
export const countUserPrompts = messages => {
  if (!Array.isArray(messages)) {
    return 0;
  }

  return messages.filter(m => m?.role === 'user' && !m?.isError).length;
};

/**
 * @param {Array<{ role?: string, text?: string, content?: string, metadata?: object }>} messages
 * @returns {object | null} last creditLimitWarnings array from assistant metadata
 */
export const getLatestCreditLimitWarnings = messages => {
  if (!Array.isArray(messages)) {
    return null;
  }

  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];

    if (m?.role === 'comparison' && !m?.isStreaming && Array.isArray(m.sides)) {
      const merged = [];

      for (const side of m.sides) {
        if (Array.isArray(side?.metadata?.creditLimitWarnings) && side.metadata.creditLimitWarnings.length > 0) {
          merged.push(...side.metadata.creditLimitWarnings);
        }
      }

      if (merged.length > 0) {
        return merged;
      }

      continue;
    }

    if (
      m?.role === 'assistant' &&
      Array.isArray(m?.metadata?.creditLimitWarnings) &&
      m.metadata.creditLimitWarnings.length > 0
    ) {
      return m.metadata.creditLimitWarnings;
    }
  }

  return null;
};

/**
 * @param {Array} messages
 * @returns {boolean}
 */
export const isSessionPromptLimitReached = messages =>
  countUserPrompts(messages) >= COMMAND_INTELLIGENCE_MAX_USER_PROMPTS;

/**
 * @param {Array} messages
 * @returns {boolean}
 */
export const isSessionContextLimitReached = messages => {
  const u = getLatestAssistantUsage(messages);

  if (!u || typeof u.inputTokens !== 'number') {
    return false;
  }

  return u.inputTokens >= COMMAND_INTELLIGENCE_MAX_INPUT_CONTEXT_TOKENS;
};
